import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import FilterDropdown from '../FilterDropdown';
import FilterPill from '../FilterPill';
import {
  ACTIVE_JOB_STATUSES,
  ALL_JOB_STATUSES,
  buildMultiChipLabel,
  JOB_STATUS_LABELS,
} from '../helpers';

import { JobStatusEnum } from '@/types/graphql';

type Props = {
  value: JobStatusEnum[] | undefined;
  onApply: (next: JobStatusEnum[] | undefined) => void;
  onClear: () => void;
};

/**
 * Hydrate the staged checkbox set from the applied filter value.
 *
 * "All active" is the implicit default: a `value` of undefined or empty means
 * the user hasn't picked anything, so we tick the 4 active statuses and leave
 * Cancelled off. Cancelled never auto-ticks — it's a deliberate opt-in.
 */
const normalize = (value: JobStatusEnum[] | undefined): Set<JobStatusEnum> => {
  if (!value || value.length === 0) return new Set(ACTIVE_JOB_STATUSES);
  return new Set(value);
};

const StatusPill = ({ value, onApply, onClear }: Props) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [staged, setStaged] = useState<Set<JobStatusEnum>>(() =>
    normalize(value),
  );

  // Re-sync staged from applied each time the dropdown opens.
  useEffect(() => {
    if (open) setStaged(normalize(value));
  }, [open, value]);

  const allActiveSelected = ACTIVE_JOB_STATUSES.every((s) => staged.has(s));
  const cancelledSelected = staged.has(JobStatusEnum.CANCELLED);

  const toggleItem = (s: JobStatusEnum) => {
    setStaged((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  // "Select All" only governs the 4 active statuses. Cancelled keeps its
  // current state so the user can pair "Select All + Cancelled" to see
  // everything, or leave Cancelled on its own.
  const toggleAllActive = () => {
    setStaged((prev) => {
      const next = new Set(prev);
      if (allActiveSelected) {
        ACTIVE_JOB_STATUSES.forEach((s) => next.delete(s));
      } else {
        ACTIVE_JOB_STATUSES.forEach((s) => next.add(s));
      }
      return next;
    });
  };

  const apply = () => {
    // Collapse "all 4 active, no Cancelled" to the implicit default
    // (undefined). The GraphQL filter then omits dashboardJobStatus and the
    // client-side strip in ShiftWeekList hides cancelled rows.
    const isImplicitDefault =
      !cancelledSelected &&
      allActiveSelected &&
      staged.size === ACTIVE_JOB_STATUSES.length;
    if (staged.size === 0 || isImplicitDefault) {
      onApply(undefined);
    } else {
      onApply(ALL_JOB_STATUSES.filter((s) => staged.has(s)));
    }
    setOpen(false);
  };

  const appliedSet = normalize(value);
  // A chip shows when the applied selection diverges from the implicit
  // "All active" default — i.e. Cancelled is on, or some active status is off.
  const appliedCancelled = appliedSet.has(JobStatusEnum.CANCELLED);
  const appliedActiveCount = ACTIVE_JOB_STATUSES.filter((s) =>
    appliedSet.has(s),
  ).length;
  const isActive =
    value !== undefined &&
    value.length > 0 &&
    (appliedCancelled || appliedActiveCount < ACTIVE_JOB_STATUSES.length);
  const selectedLabels = ALL_JOB_STATUSES.filter((s) => appliedSet.has(s)).map(
    (s) => JOB_STATUS_LABELS[s],
  );
  const { label, more } = buildMultiChipLabel(selectedLabels);

  return (
    <div className="pill-wrap" ref={wrapRef}>
      <FilterPill
        placeholder="All active"
        chipLabel={isActive ? label : undefined}
        chipMore={isActive ? more : undefined}
        open={open}
        onToggle={() => setOpen((v) => !v)}
        onClear={isActive ? onClear : undefined}
        a11yLabel="Filter by status"
      />
      <FilterDropdown
        open={open}
        onClose={() => setOpen(false)}
        onApply={apply}
        anchorRef={wrapRef}
        panelStyle={{ minWidth: 200 }}
      >
        <div
          className="dropdown-option"
          role="checkbox"
          aria-checked={allActiveSelected}
          onClick={toggleAllActive}
        >
          <span
            className={classNames('dropdown-check', {
              checked: allActiveSelected,
            })}
          >
            {allActiveSelected ? (
              <FontAwesomeIcon icon={faCheck} fixedWidth />
            ) : null}
          </span>
          <span>{allActiveSelected ? 'Unselect All' : 'Select All'}</span>
        </div>
        {ACTIVE_JOB_STATUSES.map((s) => {
          const checked = staged.has(s);
          return (
            <div
              key={s}
              className="dropdown-option"
              role="checkbox"
              aria-checked={checked}
              onClick={() => toggleItem(s)}
            >
              <span className={classNames('dropdown-check', { checked })}>
                {checked ? <FontAwesomeIcon icon={faCheck} fixedWidth /> : null}
              </span>
              {JOB_STATUS_LABELS[s]}
            </div>
          );
        })}
        <div className="dropdown-divider" />
        <div
          className="dropdown-option"
          role="checkbox"
          aria-checked={cancelledSelected}
          onClick={() => toggleItem(JobStatusEnum.CANCELLED)}
        >
          <span
            className={classNames('dropdown-check', {
              checked: cancelledSelected,
            })}
          >
            {cancelledSelected ? (
              <FontAwesomeIcon icon={faCheck} fixedWidth />
            ) : null}
          </span>
          {JOB_STATUS_LABELS[JobStatusEnum.CANCELLED]}
        </div>
      </FilterDropdown>
    </div>
  );
};

export default StatusPill;
